import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  TablePagination,
  LinearProgress,
  Box,
  Typography,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreIcon,
} from '@mui/icons-material';

const InventoryTable = ({
  inventory,
  loading,
  page,
  rowsPerPage,
  totalCount,
  onChangePage,
  onChangeRowsPerPage,
  onViewItem,
  onEditItem,
  onDeleteItem,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  const handleMenuOpen = (event, item) => {
    setAnchorEl(event.currentTarget);
    setSelectedItem(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedItem(null);
  };

  const getStatusColor = (item) => {
    if (item.quantity === 0) return 'error';
    if (item.quantity <= (item.reorderPoint || 0)) return 'warning';
    return 'success';
  };

  const getStatusText = (item) => {
    if (item.quantity === 0) return 'Out of Stock';
    if (item.quantity <= (item.reorderPoint || 0)) return 'Low Stock';
    return 'In Stock';
  };

  const getStockPercentage = (current, max) => {
    return max > 0 ? (current / max) * 100 : 0;
  };

  if (loading) {
    return (
      <Paper>
        <Box sx={{ p: 2 }}>
          <Typography>Loading inventory...</Typography>
          <LinearProgress sx={{ mt: 1 }} />
        </Box>
      </Paper>
    );
  }

  if (!inventory || inventory.length === 0) {
    return (
      <Paper>
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No inventory items found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Add some items to get started
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper>
      <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Product</TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Brand</TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>SKU</TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Category</TableCell>
              <TableCell>Stock</TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Price</TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {inventory.map((item) => (
              <TableRow key={item.id || item._id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      src={(() => {
                        // Parse images if stored as JSON string
                        let images = item.images;
                        if (typeof images === 'string') {
                          try {
                            images = JSON.parse(images);
                          } catch (e) {
                            return null;
                          }
                        }
                        if (Array.isArray(images) && images.length > 0) {
                          const imageUrl = images[0]?.url || images[0]?.secure_url || images[0];
                          // If it's a local path, prepend server URL (not API URL)
                          if (imageUrl && imageUrl.startsWith('/uploads/')) {
                            const serverUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5006').replace('/api', '');
                            return `${serverUrl}${imageUrl}`;
                          }
                          return imageUrl;
                        }
                        return null;
                      })()}
                      alt={item.name}
                      sx={{ width: 40, height: 40 }}
                    >
                      {item.name.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {item.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.description?.substring(0, 50)}
                        {item.description?.length > 50 ? '...' : ''}
                      </Typography>
                      {item.productUrl && (
                        <Typography variant="caption" display="block">
                          <a 
                            href={item.productUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ color: '#1976d2', textDecoration: 'none' }}
                          >
                            🔗 View on Store
                          </a>
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  <Typography variant="body2" fontWeight="medium">
                    {item.brand || 'N/A'}
                  </Typography>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  <Typography variant="body2" fontFamily="monospace">
                    {item.sku}
                  </Typography>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  <Typography variant="body2">
                    {item.mainCategory}
                    {item.subCategory && ` / ${item.subCategory}`}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {item.quantity || 0}
                      {item.maxQuantity && ` / ${item.maxQuantity}`}
                    </Typography>
                    {item.maxQuantity && (
                      <LinearProgress
                        variant="determinate"
                        value={getStockPercentage(item.quantity, item.maxQuantity)}
                        sx={{ mt: 0.5, height: 4, borderRadius: 2 }}
                        color={getStatusColor(item)}
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  <Typography variant="body2" fontWeight="bold">
                    ${item.retail || 0}
                  </Typography>
                  {item.cost && (
                    <Typography variant="caption" color="text.secondary">
                      Cost: ${item.cost}
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  <Chip
                    label={getStatusText(item)}
                    color={getStatusColor(item)}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, item)}
                  >
                    <MoreIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      <TablePagination
        component="div"
        count={totalCount || 0}
        page={page}
        onPageChange={onChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
      />

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            onViewItem(selectedItem);
            handleMenuClose();
          }}
        >
          <ViewIcon sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem
          onClick={() => {
            onEditItem(selectedItem);
            handleMenuClose();
          }}
        >
          <EditIcon sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            onDeleteItem(selectedItem);
            handleMenuClose();
          }}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>
    </Paper>
  );
};

export default InventoryTable;
